import { useState, useCallback, useEffect, useRef } from 'react';
import { useHARStore } from '@/stores/har-store';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { FileDropzone } from '@/components/FileDropzone';
import { Header } from '@/components/Header';
import { StatsOverview } from '@/components/StatsOverview';
import { FilterBar } from '@/components/FilterBar';
import { RequestTable } from '@/components/RequestTable';
import { WaterfallChart } from '@/components/WaterfallChart';
import { RequestDetail } from '@/components/RequestDetail';
import { PerformanceInsights } from '@/components/PerformanceInsights';
import { AIInsights } from '@/components/AIInsights';
import { ContentTypeChart } from '@/components/ContentTypeChart';
import { DomainGrouping } from '@/components/DomainGrouping';
import { SecurityAudit } from '@/components/SecurityAudit';
import { CacheAnalysis } from '@/components/CacheAnalysis';
import { ThirdPartyBreakdown } from '@/components/ThirdPartyBreakdown';
import { HARComparisonView } from '@/components/HARComparisonView';
import { TimingDistribution } from '@/components/TimingDistribution';
import { GraphQLAnalysis } from '@/components/GraphQLAnalysis';
import { WebSocketSupport } from '@/components/WebSocketSupport';
import { HTTP2HTTP3Analysis } from '@/components/HTTP2HTTP3Analysis';
import { ResourceHints } from '@/components/ResourceHints';
import { ImageOptimization } from '@/components/ImageOptimization';
import { GitCompare } from 'lucide-react';
import { DraggableDashboard, DashboardSection } from '@/components/DraggableDashboard';
import { Documentation } from '@/components/Documentation';
import { ToastContainer } from '@/components/ToastContainer';
import { KeyboardShortcutsModal } from '@/components/KeyboardShortcutsModal';
import { BasicDashboard } from '@/components/BasicDashboard';
import { cn } from '@/lib/utils';

type ViewMode = 'table' | 'domain' | 'compare';

const MIN_SIDEBAR_WIDTH = 450;
const MAX_SIDEBAR_WIDTH = 1000;
const DEFAULT_SIDEBAR_WIDTH = 650;
const DASHBOARD_LAYOUT_KEY = 'har-dashboard-layout';

// Storage key for user preferences
const USER_PREFS_KEY = 'har-insight-user-preferences';

interface StoredDashboardSectionLayout {
  id: string;
  colSpan?: 1 | 2 | 3;
  collapsed?: boolean;
}

// Component for the Requests section with view mode toggle
function RequestsContent() {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const { compareHar } = useHARStore();
  const previousCompareHar = useRef(compareHar);

  // Auto-switch to compare tab when a comparison file is loaded
  useEffect(() => {
    if (compareHar && !previousCompareHar.current) {
      setViewMode('compare');
    }
    previousCompareHar.current = compareHar;
  }, [compareHar]);

  return (
    <>
      <div className="flex items-center justify-end mb-4">
        <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('table')}
            className={cn(
              'px-3 py-1 text-xs font-medium rounded-md transition-colors',
              viewMode === 'table'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            )}
          >
            List
          </button>
          <button
            onClick={() => setViewMode('domain')}
            className={cn(
              'px-3 py-1 text-xs font-medium rounded-md transition-colors',
              viewMode === 'domain'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            )}
          >
            By Domain
          </button>
          <button
            onClick={() => setViewMode('compare')}
            disabled={!compareHar}
            className={cn(
              'px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1',
              viewMode === 'compare'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300',
              !compareHar && 'opacity-50 cursor-not-allowed'
            )}
            title={compareHar ? 'Compare HAR files' : 'Load a comparison HAR first'}
          >
            <GitCompare className="w-3 h-3" />
            Compare
            {compareHar && (
              <span className="ml-1 w-1.5 h-1.5 rounded-full bg-brand-500" />
            )}
          </button>
        </div>
      </div>
      {viewMode === 'table' && <RequestTable />}
      {viewMode === 'domain' && <DomainGrouping />}
      {viewMode === 'compare' && <HARComparisonView />}
    </>
  );
}

export default function App() {
  const { har, sidebarOpen, loadHAR, documentationOpen, setDocumentationOpen, activeTabId, viewMode } = useHARStore();
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);

  // Define initial sections order - used as base for intelligent sorting
  // Note: Everything below requests/waterfall defaults to 1 column for better density
  const initialSectionsRef = useRef<DashboardSection[]>([
    { id: 'stats', title: 'Stats Overview', colSpan: 3, component: <StatsOverview /> },
    { id: 'filter', title: 'Filters', colSpan: 3, component: <FilterBar /> },
    { id: 'requests', title: 'Requests', colSpan: 2, component: <RequestsContent /> },
    { id: 'waterfall', title: 'Waterfall', colSpan: 2, component: <WaterfallChart /> },
    { id: 'ai', title: 'AI Insights', colSpan: 1, component: <AIInsights /> },
    { id: 'perf', title: 'Performance', colSpan: 1, component: <PerformanceInsights /> },
    { id: 'security', title: 'Security', colSpan: 1, component: <SecurityAudit /> },
    { id: 'timing-dist', title: 'Timing Distribution', colSpan: 1, component: <TimingDistribution /> },
    { id: 'cache', title: 'Cache', colSpan: 1, component: <CacheAnalysis /> },
    { id: 'content', title: 'Content Types', colSpan: 1, component: <ContentTypeChart /> },
    { id: 'third-party', title: 'Third Party', colSpan: 1, component: <ThirdPartyBreakdown /> },
    { id: 'graphql', title: 'GraphQL', colSpan: 1, component: <GraphQLAnalysis /> },
    { id: 'websocket', title: 'WebSocket', colSpan: 1, component: <WebSocketSupport /> },
    { id: 'http2-http3', title: 'HTTP/2 & HTTP/3', colSpan: 1, component: <HTTP2HTTP3Analysis /> },
    { id: 'resource-hints', title: 'Resource Hints', colSpan: 1, component: <ResourceHints /> },
    { id: 'image-opt', title: 'Image Optimization', colSpan: 1, component: <ImageOptimization /> },
  ]);

  // Layout state for draggable components
  const [sections, setSections] = useState<DashboardSection[]>(() => {
    const saved = localStorage.getItem(DASHBOARD_LAYOUT_KEY);
    const initialSections = initialSectionsRef.current;

    if (saved) {
      try {
        const parsed = JSON.parse(saved) as string[] | StoredDashboardSectionLayout[];
        const savedById = new Map<string, StoredDashboardSectionLayout>();

        // Backward compatibility: old shape was `string[]` containing only order.
        if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
          for (const id of parsed) {
            savedById.set(id, { id });
          }
        } else if (Array.isArray(parsed)) {
          for (const item of parsed) {
            if (item && typeof item.id === 'string') {
              savedById.set(item.id, item);
            }
          }
        }

        const restoredSections: DashboardSection[] = [];
        for (const id of Array.from(savedById.keys())) {
          const section = initialSections.find(s => s.id === id);
          if (!section) continue;

          const persisted = savedById.get(id);
          restoredSections.push({
            ...section,
            colSpan: persisted?.colSpan ?? section.colSpan,
            collapsed: persisted?.collapsed ?? false,
          });
        }

        return restoredSections.length > 0 ? restoredSections : initialSections;
      } catch {
        return initialSections;
      }
    }
    return initialSections;
  });

  const saveLayoutState = useCallback((nextSections: DashboardSection[]) => {
    const serialized: StoredDashboardSectionLayout[] = nextSections.map(section => ({
      id: section.id,
      colSpan: section.colSpan,
      collapsed: Boolean(section.collapsed),
    }));
    localStorage.setItem(DASHBOARD_LAYOUT_KEY, JSON.stringify(serialized));
  }, []);

  const handleReorder = (newSections: DashboardSection[]) => {
    setSections(newSections);
    saveLayoutState(newSections);
  };

  const handleToggleCollapse = useCallback((id: string) => {
    setSections(prev => {
      const next = prev.map(s =>
        s.id === id ? { ...s, collapsed: !s.collapsed } : s
      );
      saveLayoutState(next);
      return next;
    });
  }, [saveLayoutState]);

  const handleColSpanChange = useCallback((id: string, colSpan: 1 | 2 | 3) => {
    setSections(prev => {
      const next = prev.map(s =>
        s.id === id ? { ...s, colSpan } : s
      );
      saveLayoutState(next);
      return next;
    });
  }, [saveLayoutState]);

  // Intelligent sorting: promote sections with data, demote empty ones
  useEffect(() => {
    if (!har) return;

    const entries = har.log.entries;
    
    // Check which sections have relevant data
    const hasGraphQL = entries.some(e => 
      e.request.url.toLowerCase().includes('graphql') ||
      e.request.postData?.text?.includes('query') ||
      e.request.postData?.text?.includes('mutation')
    );

    const hasWebSocket = entries.some(e => 
      e.request.url.startsWith('ws://') || e.request.url.startsWith('wss://')
    );

    const hasImages = entries.some(e => 
      e.response.content.mimeType?.includes('image/')
    );

    const hasResourceHints = entries.some(e => {
      const linkHeader = e.response.headers.find(h => h.name.toLowerCase() === 'link');
      return linkHeader?.value?.match(/rel=(?:"|')?(preload|prefetch|preconnect|dns-prefetch)/i);
    });

    const hasHttp2Or3 = entries.some(e => 
      e.request.httpVersion?.toLowerCase().includes('2') ||
      e.request.httpVersion?.toLowerCase().includes('3')
    );

    // Define priority based on data availability
    const sectionPriority: Record<string, number> = {
      'stats': 100,
      'filter': 95,
      'requests': 90,
      'waterfall': 85,
      'ai': entries.length > 5 ? 80 : 70,
      'perf': 75,
      'security': 70,
      'cache': 65,
      'graphql': hasGraphQL ? 60 : 20,
      'websocket': hasWebSocket ? 60 : 20,
      'http2-http3': hasHttp2Or3 ? 55 : 15,
      'image-opt': hasImages ? 50 : 10,
      'resource-hints': hasResourceHints ? 50 : 10,
      'third-party': 45,
      'content': 40,
      'timing-dist': 35,
    };

    // Only auto-sort if user hasn't manually arranged (no saved layout)
    const saved = localStorage.getItem(DASHBOARD_LAYOUT_KEY);
    if (!saved) {
      // Sort from initial sections, not from current state (which may be from a different tab)
      const initialSections = initialSectionsRef.current;
      const sorted = [...initialSections].sort((a, b) => sectionPriority[b.id] - sectionPriority[a.id]);
      
      // Adjust sizes: demote empty sections to 'third'
      const resized: DashboardSection[] = sorted.map(s => {
        const hasData = sectionPriority[s.id] >= 40; // Threshold for "has meaningful data"
        if (!hasData && s.colSpan === 2) {
          return { ...s, colSpan: 1 };
        }
        return s;
      });
      
      setSections(resized);
    }
    // Re-run when active tab changes to re-sort based on that tab's data
  }, [har, activeTabId]);

  // Monitor online/offline status
  const { isOnline } = useNetworkStatus();
  const { setOnlineStatus } = useHARStore();

  useEffect(() => {
    setOnlineStatus(isOnline);
  }, [isOnline, setOnlineStatus]);

  // Load shared HAR from URL
  useEffect(() => {
    const path = window.location.pathname;
    const shareMatch = path.match(/^\/s\/([a-zA-Z0-9]+)$/);

    if (shareMatch) {
      const shareId = shareMatch[1];

      const API_URL = import.meta.env.DEV
        ? 'http://localhost:8787'
        : (import.meta.env.VITE_API_URL || 'https://har-insight-api.thelazysre.workers.dev');

      fetch(`${API_URL}/share/${shareId}`)
        .then(async (response) => {
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Failed to load share' }));
            throw new Error(errorData.error || `Server error: ${response.status}`);
          }
          return response.text();
        })
        .then((harContent) => {
          loadHAR(harContent, `shared-${shareId}.har`);
          // Clean up URL without reloading
          window.history.replaceState({}, document.title, '/');
        })
        .catch((error) => {
          console.error('Failed to load share:', error);
          // Show error in UI
          alert(`Failed to load shared HAR: ${error instanceof Error ? error.message : 'Unknown error'}`);
        });
    }
  }, [loadHAR]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      setSidebarWidth(Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Load user preferences from localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    try {
      const prefs = localStorage.getItem(USER_PREFS_KEY);
      if (prefs) {
        const parsed = JSON.parse(prefs);
        if (parsed.sidebarWidth) {
          setSidebarWidth(Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, parsed.sidebarWidth)));
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  // Save sidebar width to localStorage when it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const prefs = {
      sidebarWidth,
    };
    localStorage.setItem(USER_PREFS_KEY, JSON.stringify(prefs));
  }, [sidebarWidth]);

  // Enable keyboard navigation
  useKeyboardNavigation({
    onShowShortcuts: () => setShortcutsModalOpen(true),
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Header />

      <main className="max-w-[1800px] mx-auto px-4 py-6">
        {!har ? (
          <div className="flex items-center justify-center min-h-[calc(100vh-120px)]">
            <FileDropzone />
          </div>
        ) : (
          <div className="animate-fade-in">
            {viewMode === 'basic' ? (
              <BasicDashboard />
            ) : (
              <DraggableDashboard
                sections={sections}
                onReorder={handleReorder}
                onToggleCollapse={handleToggleCollapse}
                onColSpanChange={handleColSpanChange}
              />
            )}
          </div>
        )}
      </main>

      {documentationOpen && <Documentation onClose={() => setDocumentationOpen(false)} />}

      {/* Request detail sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 z-50',
          isResizing ? '' : 'transition-transform duration-300 ease-out',
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        style={{ width: typeof window !== 'undefined' && window.innerWidth < 640 ? '100%' : `${sidebarWidth}px` }}
      >
        {/* Resize handle */}
        <div
          onMouseDown={handleMouseDown}
          className={cn(
            'absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-brand-500/50 transition-colors hidden sm:block',
            isResizing && 'bg-brand-500'
          )}
        />
        <RequestDetail />
      </div>

      {/* Overlay when sidebar is open - click to close */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:bg-black/20 z-40"
          onClick={() => useHARStore.getState().setSidebarOpen(false)}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer />

      {/* Keyboard shortcuts modal */}
      <KeyboardShortcutsModal
        isOpen={shortcutsModalOpen}
        onClose={() => setShortcutsModalOpen(false)}
      />
    </div>
  );
}
